import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import '../../Styles/ManagementPages.css';
import '../../Styles/CustomerManage.css';
import {
  getDrivers,
  getDriverById,
  updateDriver,
  getPendingDrivers,
  approvePendingDriver,
  rejectPendingDriver,
  getPendingDriverById,
  updatePendingDriver
} from '../../Services/Operations/ProductAPI';
import { toast } from 'react-toastify';

const SkeletonStatsCard = () => (
  <div className="stat-card skeleton">
    <div className="skeleton-title"></div>
    <div className="skeleton-value"></div>
    <div className="skeleton-subtitle"></div>
  </div>
);

const SkeletonTableRow = () => (
  <tr className="skeleton-row">
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-badge"></div></td>
    <td><div className="skeleton-actions"></div></td>
  </tr>
);

const SkeletonTable = () => (
  <div className="table-container skeleton">
    <table className="data-table">
      <thead>
        <tr>
          <th>No.</th>
          <th>Name</th>
          <th>Mobile</th>
          <th>License</th>
          <th>Experience</th>
          <th>Bus Owner</th>
          <th>Assigned Bus</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
          <SkeletonTableRow key={i} />
        ))}
      </tbody>
    </table>
  </div>
);

const SkeletonModal = () => (
  <div className="modal-overlay">
    <div className="modal-content1 skeleton-modal">
      <div className="modal-header">
        <div className="skeleton-title"></div>
        <div className="skeleton-close"></div>
      </div>
      <div className="modal-body">
        <div className="skeleton-profile-image"></div>
        {[1, 2, 3, 4, 5, 6, 7].map((i) => (
          <div key={i} className="skeleton-detail-row">
            <div className="skeleton-label"></div>
            <div className="skeleton-value"></div>
          </div>
        ))}
      </div>
      <div className="modal-footer">
        <div className="skeleton-button"></div>
      </div>
    </div>
  </div>
);

const SkeletonEditModal = () => (
  <div className="modal-overlay">
    <div className="modal-content1 edit-modal skeleton-modal">
      <div className="modal-header">
        <div className="skeleton-title"></div>
        <div className="skeleton-close"></div>
      </div>
      <div className="modal-body">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="form-group">
            <div className="skeleton-label"></div>
            <div className="skeleton-input"></div>
          </div>
        ))}
      </div>
      <div className="modal-footer">
        <div className="skeleton-button"></div>
        <div className="skeleton-button"></div>
      </div>
    </div>
  </div>
);

const DriverManagement = () => {
  const { token } = useSelector(state => state.user);
  const [isEditingPending, setIsEditingPending] = useState(false);

  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [viewMode, setViewMode] = useState('regular');
  const [pendingDrivers, setPendingDrivers] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);

  const [editingDriver, setEditingDriver] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [viewingDriver, setViewingDriver] = useState(null);
  const [error, setError] = useState(null);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDrivers, setTotalDrivers] = useState(0);
  const [pageSize] = useState(10);

  const getSequentialNumber = (index) => {
    return (currentPage - 1) * pageSize + index + 1;
  };

  const fetchDrivers = async () => {
    try {
      setSearching(true);
      setLoading(true);
      setError(null);

      const response = await getDrivers(token, currentPage, pageSize, searchTerm);

      if (response && response.drivers) {
        const transformedDrivers = response.drivers.map((driver, index) => ({
          id: driver._id,
          serialNo: getSequentialNumber(index),
          profileImage: driver.profileImage,
          name: driver.name || '',
          mobileNumber: driver.mobileNumber || '',
          licenseNumber: driver.licenseNumber || '',
          experience: driver.experience || 0,
          assignedBus: driver.assignedBus || null,
          busOwner: driver.busOwner || null,
          isActive: driver.isActive || false,
          joined: new Date(driver.createdAt).toISOString().split('T')[0],
          status: driver.isActive ? 'Active' : 'Inactive',
          isPending: false,
          source: 'regular'
        }));

        setDrivers(transformedDrivers);
        setTotalPages(response.totalPages || 1);
        setTotalDrivers(response.total || 0);
      }
    } catch (err) {
      setError('Failed to fetch drivers.');
      console.error('Error fetching drivers:', err);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  const fetchPendingDrivers = async () => {
    try {
      setLoadingPending(true);
      setError(null);

      const response = await getPendingDrivers(token);

      if (response && response.success) {
        const transformedDrivers = response.data.map((driver, index) => ({
          id: driver._id,
          pendingId: driver._id,
          serialNo: index + 1,
          profileImage: driver.profileImage || '',
          name: driver.name || '',
          mobileNumber: driver.mobileNumber || '',
          licenseNumber: driver.licenseNumber || '',
          experience: driver.experience || 0,
          assignedBus: driver.assignedBus || null,
          busOwner: driver.busOwner || null,
          isActive: false,
          joined: new Date(driver.createdAt).toISOString().split('T')[0],
          status: 'Pending',
          isPending: true,
          source: 'pending',
          submittedBy: driver.submittedBy,
          submittedByName: driver.submittedByName,
          submittedByEmail: driver.submittedByEmail,
          operationType: driver.operationType,
          approvalStatus: driver.approvalStatus,
          originalData: driver.originalData
        }));

        setPendingDrivers(transformedDrivers);
        setTotalDrivers(transformedDrivers.length);
        setTotalPages(1);
      } else {
        setPendingDrivers([]);
        setTotalDrivers(0);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Error fetching pending drivers:', err);
      setError('Failed to fetch pending drivers. Please try again.');
      setPendingDrivers([]);
    } finally {
      setLoadingPending(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'regular') {
      fetchDrivers();
    } else {
      fetchPendingDrivers();
    }
  }, [viewMode, currentPage, searchTerm]);

  const refreshPendingDrivers = async () => {
    await fetchPendingDrivers();
  };

  const getCurrentDrivers = () => {
    return viewMode === 'regular' ? drivers : pendingDrivers;
  };

  const getCurrentLoading = () => {
    return viewMode === 'regular' ? loading : loadingPending;
  };

  const currentDrivers = getCurrentDrivers();
  const isLoading = getCurrentLoading();

  const handleApprovePendingDriver = async (pendingId) => {
    try {
      setError(null);
      const response = await approvePendingDriver(pendingId, token);

      if (response.success) {
        toast.success('Pending driver approved successfully!');
        setPendingDrivers(prev => prev.filter(driver => driver.id !== pendingId));
        setTotalDrivers(prev => prev - 1);
        await fetchDrivers();
      } else {
        throw new Error(response.message || 'Failed to approve pending driver');
      }
    } catch (err) {
      console.error('Error approving pending driver:', err);
      setError('Failed to approve pending driver. Please try again.');
      toast.error('Failed to approve pending driver. Please try again.');
    }
  };

  const handleRejectPendingDriver = async (pendingId) => {
    if (!window.confirm('Are you sure you want to reject this pending driver?')) return;

    try {
      setError(null);
      const response = await rejectPendingDriver(pendingId, token);

      if (response.success) {
        toast.success('Pending driver rejected successfully!');
        setPendingDrivers(prev => prev.filter(driver => driver.id !== pendingId));
        setTotalDrivers(prev => prev - 1);
      } else {
        throw new Error(response.message || 'Failed to reject pending driver');
      }
    } catch (err) {
      console.error('Error rejecting pending driver:', err);
      setError('Failed to reject pending driver. Please try again.');
      toast.error('Failed to reject pending driver. Please try again.');
    }
  };

  const handleViewDriver = async (driver) => {
    try {
      setIsFetchingDetails(true);
      setError(null);

      let response;

      if (driver.isPending) {
        response = await getPendingDriverById(driver.id, token);
      } else {
        response = await getDriverById(driver.id, token);
      }

      if (response.success) {
        const driverData = response.data;
        setViewingDriver({
          id: driverData._id,
          name: driverData.name || '',
          mobileNumber: driverData.mobileNumber || '',
          licenseNumber: driverData.licenseNumber || '',
          experience: driverData.experience || 0,
          email: driverData.email || '',
          address: driverData.address || '',
          isActive: driverData.isActive,
          assignedBus: driverData.assignedBus || null,
          busOwner: driverData.busOwner || null,
          profileImage: driverData.profileImage,
          createdAt: new Date(driverData.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          updatedAt: new Date(driverData.updatedAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          isPending: driver.isPending,
          submittedBy: driverData.submittedBy,
          submittedByName: driverData.submittedByName,
          operationType: driverData.operationType
        });
      } else {
        setError('Failed to fetch driver details.');
      }
    } catch (err) {
      console.error('Error fetching driver details:', err);
      setError('Failed to fetch driver details.');
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const handleEditDriver = async (driverId) => {
    try {
      setIsFetchingDetails(true);
      setError(null);

      const response = await getDriverById(driverId, token);

      if (response.success) {
        const driver = response.data;
        setEditingDriver(driver._id);
        setEditForm({
          name: driver.name || '',
          mobileNumber: driver.mobileNumber || '',
          licenseNumber: driver.licenseNumber || '',
          experience: driver.experience || 0,
          isActive: driver.isActive || true
        });
      } else {
        setError('Failed to fetch driver details for editing.');
      }
    } catch (err) {
      console.error('Error fetching driver details for editing:', err);
      setError('Failed to fetch driver details for editing.');
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const handleEditPendingDriver = async (driverId) => {
    try {
      setIsFetchingDetails(true);
      setError(null);
      setIsEditingPending(true);

      const response = await getPendingDriverById(driverId, token);

      if (response.success) {
        const driver = response.data;
        setEditingDriver(driver._id);
        setEditForm({
          name: driver.name || '',
          mobileNumber: driver.mobileNumber || '',
          licenseNumber: driver.licenseNumber || '',
          experience: driver.experience || 0,
          isActive: false
        });
      } else {
        setError('Failed to fetch pending driver details for editing.');
      }
    } catch (err) {
      console.error('Error fetching pending driver details for editing:', err);
      setError('Failed to fetch pending driver details for editing.');
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      setError(null);

      const updateData = {
        name: editForm.name,
        mobileNumber: editForm.mobileNumber,
        licenseNumber: editForm.licenseNumber,
        experience: editForm.experience,
        ...(isEditingPending ? {} : { isActive: editForm.isActive })
      };

      const response = isEditingPending
        ? await updatePendingDriver(editingDriver, updateData, token)
        : await updateDriver(editingDriver, updateData, token);

      if (response.success) {
        if (isEditingPending) {
          setPendingDrivers(prevDrivers =>
            prevDrivers.map(driver =>
              driver.id === editingDriver ? {
                ...driver,
                name: response.data.name,
                mobileNumber: response.data.mobileNumber,
                licenseNumber: response.data.licenseNumber,
                experience: response.data.experience,
              } : driver
            )
          );
          toast.success('Pending driver updated successfully!');
        } else {
          setDrivers(prevDrivers =>
            prevDrivers.map(driver =>
              driver.id === editingDriver ? {
                ...driver,
                name: response.data.name,
                mobileNumber: response.data.mobileNumber,
                licenseNumber: response.data.licenseNumber,
                experience: response.data.experience,
                status: response.data.isActive ? 'Active' : 'Inactive',
                isActive: response.data.isActive
              } : driver
            )
          );
          alert('Driver updated successfully!');
        }

        setEditingDriver(null);
        setEditForm({});
        setIsEditingPending(false);
      } else {
        setError(response.message || 'Failed to update driver.');
      }
    } catch (err) {
      console.error('Error updating driver:', err);
      setError(err.response?.data?.message || 'Failed to update driver. Please try again.');
      if (!isEditingPending) alert('Failed to update driver. Please try again.');
      else toast.error('Failed to update pending driver. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditingDriver(null);
    setEditForm({});
    setError(null);
    setIsEditingPending(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const closeViewModal = () => {
    setViewingDriver(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    setSearchTerm('');
    setCurrentPage(1);
    if (viewMode === 'regular') {
      fetchDrivers();
    } else {
      fetchPendingDrivers();
    }
  };

  const handleExportCSV = () => {
    if (currentDrivers.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = ['Serial No.', 'ID', 'Name', 'Mobile', 'License', 'Experience', 'Bus Owner', 'Assigned Bus', 'Status'];
    const csvData = currentDrivers.map(driver => [
      driver.serialNo,
      driver.id,
      driver.name || '',
      driver.mobileNumber || '',
      driver.licenseNumber || '',
      driver.experience || '',
      driver.busOwner?.name || 'N/A',
      driver.assignedBus?.busNumber || 'Not Assigned',
      driver.status
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `drivers-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    alert('CSV exported successfully!');
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const filteredDrivers = currentDrivers.filter(driver =>
    (driver.name && driver.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (driver.mobileNumber && driver.mobileNumber.includes(searchTerm)) ||
    (driver.licenseNumber && driver.licenseNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (driver.busOwner?.name && driver.busOwner.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (isLoading) {
    return (
      <div className="management-page">
        <div className="page-header">
          <div className="skeleton-header-title"></div>
          <div className="header-actions">
            <div className="skeleton-search"></div>
            <div className="skeleton-button"></div>
            <div className="skeleton-button"></div>
          </div>
        </div>

        <div className="stats-cards">
          {[1, 2, 3, 4].map((i) => (
            <SkeletonStatsCard key={i} />
          ))}
        </div>

        <SkeletonTable />

        <div className="pagination-container">
          <div className="skeleton-pagination-info"></div>
          <div className="skeleton-pagination-controls"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Driver Management</h1>
        <div className="header-actions">
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search drivers by name, mobile, license or bus owner..."
              className="search-input"
              value={searchTerm}
              onChange={handleSearchChange}
              style={{ width: "70%" }}
            />
            {searching && (
              <span style={{
                position: 'absolute', right: '10px', top: '50%',
                transform: 'translateY(-50%)', fontSize: '12px', color: '#888'
              }}>
                searching...
              </span>
            )}
          </div>

          <button className="refresh-btn" onClick={handleRefresh}>
            Refresh
          </button>
          <button className="export-btn" onClick={handleExportCSV}>
            Export CSV
          </button>
        </div>
      </div>

      <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px', textAlign: 'center' }}>
        {viewMode === 'regular' ? (
          <>Page: {currentPage} | Total Pages: {totalPages} | Total Drivers: {totalDrivers}</>
        ) : (
          <>Pending Drivers: {pendingDrivers.length} | {loadingPending ? 'Loading...' : ''}</>
        )}
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="stats-cards">
        <div className="stat-card">
          <h3>Total {viewMode === 'regular' ? 'Drivers' : 'Pending Drivers'}</h3>
          <p>{totalDrivers}</p>
          <span>{viewMode === 'regular' ? 'All registered drivers' : 'Awaiting approval'}</span>
        </div>
        <div className="stat-card">
          <h3>Active Drivers</h3>
          <p>{viewMode === 'regular' ? drivers.filter(d => d.status === 'Active').length : 0}</p>
          <span>Active drivers</span>
        </div>
        <div className="stat-card">
          <h3>With Assigned Bus</h3>
          <p>{viewMode === 'regular' ? drivers.filter(d => d.assignedBus).length : 0}</p>
          <span>Drivers with bus assignment</span>
        </div>
        <div className="stat-card">
          <h3>Search Results</h3>
          <p>{filteredDrivers.length}</p>
          <span>drivers found</span>
        </div>
      </div>


      <div className="view-toggle-group" style={{ display: 'flex', gap: '10px', alignItems: 'center', margin: '0 15px' }}>
        <button
          className={`toggle-btn ${viewMode === 'regular' ? 'active' : ''}`}
          onClick={() => {
            setViewMode('regular');
            setCurrentPage(1);
            setSearchTerm('');
          }}
          style={{
            padding: '8px 20px',
            borderRadius: '20px',
            border: '2px solid #007bff',
            backgroundColor: viewMode === 'regular' ? '#007bff' : 'transparent',
            color: viewMode === 'regular' ? 'white' : '#007bff',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.3s'
          }}
        >
          👤 Regular
        </button>
        <button
          className={`toggle-btn ${viewMode === 'pending' ? 'active' : ''}`}
          onClick={() => {
            setViewMode('pending');
            setCurrentPage(1);
            setSearchTerm('');
          }}
          style={{
            padding: '8px 20px',
            borderRadius: '20px',
            border: '2px solid #ff9800',
            backgroundColor: viewMode === 'pending' ? '#ff9800' : 'transparent',
            color: viewMode === 'pending' ? 'white' : '#ff9800',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.3s',
            position: 'relative'
          }}
        >
          📋 Executive Submitted
          {viewMode === 'pending' && pendingDrivers.length > 0 && (
            <span style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              backgroundColor: 'red',
              color: 'white',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              fontSize: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold'
            }}>
              {pendingDrivers.length}
            </span>
          )}
        </button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Name</th>
              <th>Mobile</th>
              <th>License</th>
              <th>Experience</th>
              <th>Bus Owner</th>
              <th>Assigned Bus</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredDrivers.length > 0 ? (
              filteredDrivers.map(driver => (
                <tr key={driver.id} className={driver.isPending ? 'pending-row' : ''}>
                  <td>{driver.serialNo}</td>
                  <td>
                    {!driver.isPending && editingDriver === driver.id ? (
                      <input
                        type="text"
                        name="name"
                        value={editForm.name || ''}
                        onChange={handleInputChange}
                        className="edit-input"
                        placeholder="Name"
                      />
                    ) : (
                      <>
                        {driver.name || 'N/A'}

                      </>
                    )}
                  </td>
                  <td>
                    {!driver.isPending && editingDriver === driver.id ? (
                      <input
                        type="text"
                        name="mobileNumber"
                        value={editForm.mobileNumber || ''}
                        onChange={handleInputChange}
                        className="edit-input"
                        placeholder="Mobile Number"
                      />
                    ) : (
                      driver.mobileNumber || 'N/A'
                    )}
                  </td>
                  <td>
                    {!driver.isPending && editingDriver === driver.id ? (
                      <input
                        type="text"
                        name="licenseNumber"
                        value={editForm.licenseNumber || ''}
                        onChange={handleInputChange}
                        className="edit-input"
                        placeholder="License Number"
                      />
                    ) : (
                      driver.licenseNumber || 'N/A'
                    )}
                  </td>
                  <td>
                    {!driver.isPending && editingDriver === driver.id ? (
                      <input
                        type="number"
                        name="experience"
                        value={editForm.experience || ''}
                        onChange={handleInputChange}
                        className="edit-input"
                        placeholder="Years"
                        min="0"
                      />
                    ) : (
                      driver.experience ? `${driver.experience} years` : 'N/A'
                    )}
                  </td>
                  <td>
                    {driver.busOwner?.name || 'N/A'}
                  </td>
                  <td>
                    {driver.assignedBus ? (
                      <div className="bus-info">
                        <div><strong>{driver.assignedBus.busNumber}</strong></div>
                        <small>{driver.assignedBus.busName}</small>
                      </div>
                    ) : 'Not Assigned'}
                  </td>
                  <td>
                    {!driver.isPending && editingDriver === driver.id ? (
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          name="isActive"
                          checked={editForm.isActive || false}
                          onChange={handleInputChange}
                        />
                        <span className="checkbox-text">
                          {editForm.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </label>
                    ) : (
                      <span className={`status-badge ${driver.isPending ? 'pending' : driver.status?.toLowerCase() || 'inactive'}`}>
                        {driver.isPending ? 'Pending Approval' : driver.status || 'Inactive'}
                      </span>
                    )}
                  </td>
                  <td>
                    {!driver.isPending ? (
                      editingDriver === driver.id ? (<div className="edit-actions">
                        <button className="action-btn save" onClick={handleSaveEdit} disabled={isFetchingDetails}>
                          {isFetchingDetails ? 'Saving...' : 'Save'}
                        </button>
                        <button className="action-btn cancel" onClick={handleCancelEdit} disabled={isFetchingDetails}>
                          Cancel
                        </button>
                      </div>) : (<div className="action-buttons">
                        <button
                          className="action-btn view"
                          onClick={() => handleViewDriver(driver)}
                          disabled={isFetchingDetails}
                        >
                          {isFetchingDetails ? 'Loading...' : 'View'}
                        </button>
                        <button
                          className="action-btn edit"
                          onClick={() => handleEditDriver(driver.id)}
                          disabled={isFetchingDetails}
                        >
                          {isFetchingDetails ? 'Loading...' : 'Edit'}
                        </button>
                      </div>)
                    ) : (
                      <td>
                        {!driver.isPending ? (
                          editingDriver === driver.id ? (<div className="edit-actions">
                            <button className="action-btn save" onClick={handleSaveEdit} disabled={isFetchingDetails}>
                              {isFetchingDetails ? 'Saving...' : 'Save'}
                            </button>
                            <button className="action-btn cancel" onClick={handleCancelEdit} disabled={isFetchingDetails}>
                              Cancel
                            </button>
                          </div>) : (<div className="action-buttons">
                            <button className="action-btn view" onClick={() => handleViewDriver(driver)} style={{ backgroundColor: '#17a2b8' }}>
                              View
                            </button>
                            <button className="action-btn edit" onClick={() => handleEditDriver(driver.id)} disabled={isFetchingDetails} style={{ backgroundColor: '#6f42c1', color: 'white' }}>
                              {isFetchingDetails ? 'Loading...' : 'Edit'}
                            </button>
                          </div>)
                        ) : (
                          <div className="action-buttons">
                            <button className="action-btn view" onClick={() => handleViewDriver(driver)} style={{ backgroundColor: '#17a2b8' }}>
                              View
                            </button>
                            <button
                              className="action-btn edit"
                              onClick={() => handleEditPendingDriver(driver.id)}
                              disabled={isFetchingDetails}
                              style={{ backgroundColor: '#6f42c1', color: 'white' }}
                            >
                              {isFetchingDetails ? 'Loading...' : 'Edit'}
                            </button>
                            <button className="action-btn approve" onClick={() => handleApprovePendingDriver(driver.id)} style={{ backgroundColor: '#28a745' }}>
                              Approve
                            </button>
                            <button className="action-btn reject" onClick={() => handleRejectPendingDriver(driver.id)} style={{ backgroundColor: '#dc3545' }}>
                              Reject
                            </button>
                            {driver.submittedByName && (
                              <div style={{
                                fontSize: '10px',
                                color: '#666',
                                marginTop: '4px',
                                display: 'block'
                              }}>
                                By: {driver.submittedByName}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="no-data">
                  <p>
                    {viewMode === 'regular'
                      ? `No drivers found ${searchTerm ? 'matching your search' : ''}.`
                      : 'No pending drivers from executive submissions.'}
                  </p>
                  {searchTerm && (
                    <button className="clear-filters-link" onClick={handleRefresh}>
                      Clear search to see all drivers
                    </button>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>


      {viewMode === 'regular' && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {drivers.length} of {totalDrivers} drivers
            (Page {currentPage} of {totalPages})
            {searchTerm && <span className="filtered-indicator"> • Search: "{searchTerm}"</span>}
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            <div className="page-numbers">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNumber;
                if (totalPages <= 5) {
                  pageNumber = i + 1;
                } else if (currentPage <= 3) {
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + i;
                } else {
                  pageNumber = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNumber}
                    className={`page-number ${pageNumber === currentPage ? 'active' : ''}`}
                    onClick={() => handlePageClick(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>

            <button
              className="pagination-btn"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}


      {viewingDriver && !isFetchingDetails && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Driver Details</h3>
              <button className="modal-close-btn" onClick={closeViewModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="customer-details">
                <div className="customer-profile-image">
                  <img
                    src={viewingDriver.profileImage ?
                      `${process.env.REACT_APP_BASE_URL}/uploads/${viewingDriver.profileImage}` :
                      ''}
                    alt={viewingDriver.name || 'Driver'}
                    className="profile-image-large"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '';
                    }}
                  />
                </div>
                <div className="detail-row">
                  <label>Name:</label>
                  <span>{viewingDriver.name || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <label>Mobile Number:</label>
                  <span>{viewingDriver.mobileNumber || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <label>License Number:</label>
                  <span>{viewingDriver.licenseNumber || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <label>Experience:</label>
                  <span>{viewingDriver.experience} years</span>
                </div>
                <div className="detail-row">
                  <label>Bus Owner:</label>
                  <span>
                    {viewingDriver.busOwner ? (
                      <div className="bus-owner-info">
                        <strong>{viewingDriver.busOwner.name}</strong>
                        <small>{viewingDriver.busOwner.phone}</small>
                      </div>
                    ) : 'N/A'}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Assigned Bus:</label>
                  <span>
                    {viewingDriver.assignedBus ? (
                      <div className="bus-info">
                        <strong>{viewingDriver.assignedBus.busNumber}</strong>
                        <small>{viewingDriver.assignedBus.busName}</small>
                      </div>
                    ) : 'Not Assigned'}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Created Date:</label>
                  <span>{viewingDriver.createdAt}</span>
                </div>
                <div className="detail-row">
                  <label>Last Updated:</label>
                  <span>{viewingDriver.updatedAt}</span>
                </div>
                <div className="detail-row">
                  <label>Status:</label>
                  <span className={`status-badge ${viewingDriver.isActive ? 'active' : 'inactive'}`}>
                    {viewingDriver.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-close-btn secondary" onClick={closeViewModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {viewingDriver && isFetchingDetails && <SkeletonModal />}


      {editingDriver && !isFetchingDetails && (
        <div className="modal-overlay" onClick={handleCancelEdit}>
          <div className="modal-content1 edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{isEditingPending ? 'Edit Pending Driver' : 'Edit Driver'}</h3>
              <button className="modal-close-btn" onClick={handleCancelEdit}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="edit-form">
                <div className="form-group">
                  <label>Name:</label>
                  <input
                    type="text"
                    name="name"
                    value={editForm.name || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Driver Name"
                  />
                </div>
                <div className="form-group">
                  <label>Mobile Number:</label>
                  <input
                    type="text"
                    name="mobileNumber"
                    value={editForm.mobileNumber || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Mobile Number"
                  />
                </div>
                <div className="form-group">
                  <label>License Number:</label>
                  <input
                    type="text"
                    name="licenseNumber"
                    value={editForm.licenseNumber || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="License Number"
                  />
                </div>
                <div className="form-group">
                  <label>Experience (years):</label>
                  <input
                    type="number"
                    name="experience"
                    value={editForm.experience || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Years of experience"
                    min="0"
                    step="0.5"
                  />
                </div>
                {!isEditingPending && (
                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={editForm.isActive || false}
                        onChange={handleInputChange}
                      />
                      <span className="checkbox-text">Active Driver</span>
                    </label>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="action-btn cancel" onClick={handleCancelEdit}>
                Cancel
              </button>
              <button className="action-btn save" onClick={handleSaveEdit}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}


      {editingDriver && isFetchingDetails && <SkeletonEditModal />}
    </div>
  );
};

export default DriverManagement;